import React, { useState, useEffect } from 'react';
import Table from '../components/common/Table';
import Badge from '../components/common/Badge';
import EmployeeLimitModal from '../components/admin/EmployeeLimitModal';
import EditCompanyModal from '../components/company/EditCompanyModal';
import { employeeApi } from '../api/employeeApi';
import { companyApi } from '../api/companyApi';
import { formatDateTime, formatDate } from '../utils/formatDate';
import { Users, ShieldAlert, CheckCircle, XCircle, Clock, Building2, Edit, ListFilter } from 'lucide-react';
import toast from 'react-hot-toast';

const EmployeeLimits = () => {
  const [activeTab, setActiveTab] = useState('all-limits'); // 'all-limits' | 'requests'
  const [companies, setCompanies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // Modals state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [compRes, reqRes] = await Promise.all([
        companyApi.getCompanies({ limit: 100 }),
        employeeApi.getLimitRequests({ status: statusFilter })
      ]);
      setCompanies(compRes?.data?.companies || compRes?.companies || []);
      setRequests(reqRes?.data || reqRes || []);
    } catch (err) {
      toast.error('Failed to fetch limit data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [statusFilter]);

  const handleUpdateCompany = async (companyId, formData) => {
    setSubmitting(true);
    try {
      await companyApi.updateCompany(companyId, formData);
      toast.success('Company employee limit updated successfully!');
      setIsEditOpen(false);
      setCompanyToEdit(null);
      fetchAllData();
    } catch (err) {
      toast.error(err.message || 'Failed to update limit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (requestId, newLimit, note) => {
    setSubmitting(true);
    try {
      await employeeApi.approveLimitRequest(requestId, { newLimit, note });
      toast.success(`Approved limit increase to ${newLimit}!`);
      setSelectedRequest(null);
      fetchAllData();
    } catch (err) {
      toast.error(err.message || 'Failed to approve request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (requestId, note) => {
    setSubmitting(true);
    try {
      await employeeApi.rejectLimitRequest(requestId, { note });
      toast.error('Limit increase request rejected');
      setSelectedRequest(null);
      fetchAllData();
    } catch (err) {
      toast.error(err.message || 'Failed to reject request');
    } finally {
      setSubmitting(false);
    }
  };

  // Columns for All Company Limits
  const companyColumns = [
    {
      header: 'Company Tenant',
      accessor: 'name',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
            alt={row.name}
            className="w-10 h-10 rounded-xl object-cover border border-slate-100"
          />
          <div>
            <p className="font-semibold text-slate-900 font-display">{row.name}</p>
            <p className="text-xs text-slate-500">{row.industry}</p>
          </div>
        </div>
      )
    },
    {
      header: 'ID Prefix',
      accessor: 'employeeIdPrefix',
      cell: (row) => (
        <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-700">
          {row.employeeIdPrefix}
        </span>
      )
    },
    {
      header: 'Assigned Admin',
      accessor: 'adminId',
      cell: (row) =>
        row.adminId ? (
          <div>
            <p className="font-medium text-slate-900 text-xs">{row.adminId.name}</p>
            <p className="text-[11px] text-slate-500">{row.adminId.email}</p>
          </div>
        ) : (
          <span className="text-slate-500 text-xs italic">Unassigned</span>
        )
    },
    {
      header: 'Employee Limit & Usage',
      accessor: 'currentEmployeeCount',
      cell: (row) => {
        const limit = row.employeeLimit || 50;
        const current = row.currentEmployeeCount || 0;
        return (
          <span className="font-semibold text-slate-900 text-xs px-3 py-1.5 rounded-xl bg-white border border-slate-100 inline-block font-mono">
            {current} / {limit} Max
          </span>
        );
      }
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => <Badge variant={row.status}>{row.status}</Badge>
    },
    {
      header: 'Actions',
      accessor: '_id',
      cell: (row) => (
        <button
          onClick={() => {
            setCompanyToEdit(row);
            setIsEditOpen(true);
          }}
          className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold text-xs flex items-center space-x-1.5 transition shadow-md shadow-emerald-600/20"
        >
          <Edit className="w-3.5 h-3.5" />
          <span>Adjust Limit</span>
        </button>
      )
    }
  ];

  // Columns for Limit Requests
  const requestColumns = [
    {
      header: 'Company Tenant',
      accessor: 'companyId',
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.companyId?.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
            alt={row.companyId?.name}
            className="w-9 h-9 rounded-xl object-cover border border-slate-100"
          />
          <div>
            <p className="font-semibold text-slate-900 font-display">{row.companyId?.name || 'Unknown'}</p>
            <p className="text-[11px] text-slate-500">Admin: {row.senderId?.name || 'N/A'}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Current Lock',
      accessor: 'limitDetails.currentLimit',
      cell: (row) => (
        <span className="font-semibold text-slate-600 text-xs">
          {row.limitDetails?.currentLimit || row.companyId?.employeeLimit || 50} Max
        </span>
      )
    },
    {
      header: 'Requested Limit',
      accessor: 'limitDetails.requestedLimit',
      cell: (row) => (
        <span className="font-bold text-emerald-600 text-sm">
          {row.limitDetails?.requestedLimit}
        </span>
      )
    },
    {
      header: 'Reason',
      accessor: 'limitDetails.reason',
      cell: (row) => (
        <span className="text-xs text-slate-500 line-clamp-1 italic max-w-xs">
          "{row.limitDetails?.reason || row.message}"
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'limitDetails.status',
      cell: (row) => <Badge variant={row.limitDetails?.status}>{row.limitDetails?.status}</Badge>
    },
    {
      header: 'Requested At',
      accessor: 'createdAt',
      cell: (row) => <span className="text-xs text-slate-500">{formatDateTime(row.createdAt)}</span>
    },
    {
      header: 'Action',
      accessor: '_id',
      cell: (row) => (
        <button
          onClick={() => setSelectedRequest(row)}
          className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition ${
            row.limitDetails?.status === 'Pending'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md shadow-emerald-600/30'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {row.limitDetails?.status === 'Pending' ? 'Review Request' : 'View Resolution'}
        </button>
      )
    }
  ];

  const pendingRequestsCount = requests.filter(r => r.limitDetails?.status === 'Pending').length;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900 font-display">Employee Count Limits</h2>
          <p className="text-xs text-slate-500 mt-1">
            Manage company employee count limits and action limit increase requests
          </p>
        </div>
      </div>

      {/* Tabs bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-2 rounded-2xl bg-white border border-slate-100 shadow-sm">
        <div className="flex items-center space-x-2 w-full sm:w-auto">
          <button
            onClick={() => setActiveTab('all-limits')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 ${
              activeTab === 'all-limits'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-600/30'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span>All Company Limits ({companies.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 ${
              activeTab === 'requests'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-600/30'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <ShieldAlert className="w-4 h-4" />
            <span>Limit Increase Requests</span>
            {pendingRequestsCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                {pendingRequestsCount}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'requests' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition w-full sm:w-auto"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending Only</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        )}
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="py-16 text-center text-xs text-slate-500">Loading data...</div>
      ) : activeTab === 'all-limits' ? (
        companies.length === 0 ? (
          <div className="p-12 text-center bg-white/50 rounded-2xl border border-slate-100 text-xs text-slate-500">
            No company tenants found.
          </div>
        ) : (
          <Table columns={companyColumns} data={companies} />
        )
      ) : requests.length === 0 ? (
        <div className="p-12 text-center bg-white/50 rounded-2xl border border-slate-100 text-xs text-slate-500">
          No employee limit increase requests found.
        </div>
      ) : (
        <Table columns={requestColumns} data={requests} />
      )}

      {/* Edit Company Limit Modal */}
      <EditCompanyModal
        company={companyToEdit}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setCompanyToEdit(null);
        }}
        onSubmit={handleUpdateCompany}
        loading={submitting}
      />

      {/* Review Request Modal */}
      <EmployeeLimitModal
        request={selectedRequest}
        isOpen={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        loading={submitting}
      />
    </div>
  );
};

export default EmployeeLimits;
